/* jshint esversion: 6 */

/*
 * Candidate feeds for the "add this social to your feeds" flow.
 *
 * In the route model an input *is* a feed, and a social publishes a feed by
 * being an output of the route that input belongs to. The candidates for a
 * given social are therefore the inputs of every route the social does not
 * already output to — a route it already outputs to has nothing left to add.
 *
 * Routes the social is itself an input of are out too: publishing a feed back
 * into the account it was pulled from is a posting loop, and the server rejects
 * the output outright ("Cannot attach an input to the same output").
 */

const MINUTE = 60,
    HOUR = 3600,
    DAY = 86400,
    WEEK = 604800,
    MONTH = 2592000;

export const RECOMMENDED_COUNT = 3;

// Input::clampVelocity's lower bound — the value a feed reports when it has no
// velocity history and when it has published nothing, indistinguishably.
export const VELOCITY_FLOOR = 0.1;

/**
 * Compact relative time ("4m ago", "2h ago", "3w ago") for a unix timestamp in
 * seconds. Returns '' for a feed that has never been pulled, so callers can
 * leave the slot empty rather than claim it was updated at the epoch.
 */
export const relativeTime = (unixTs, nowMs = Date.now()) => {
    const ts = parseInt(unixTs, 10);

    if (!ts) {
        return '';
    }

    const seconds = Math.max(0, Math.floor(nowMs / 1000) - ts);

    if (seconds < MINUTE) {
        return 'just now';
    }

    if (seconds < HOUR) {
        return `${Math.floor(seconds / MINUTE)}m ago`;
    }

    if (seconds < DAY) {
        return `${Math.floor(seconds / HOUR)}h ago`;
    }

    if (seconds < WEEK) {
        return `${Math.floor(seconds / DAY)}d ago`;
    }

    if (seconds < MONTH) {
        return `${Math.floor(seconds / WEEK)}w ago`;
    }

    return `${Math.floor(seconds / MONTH)}mo ago`;
};

/**
 * Renders an input's velocity as the picker's cadence line.
 *
 * Velocity is a 7-day item rate (Input::getVelocity), clamped server side to
 * VELOCITY_FLOOR–1000. The floor is why a feed at the floor renders nothing: a
 * feed with no stats rows at all and one that has been dead for a week both come
 * back as 0.1, and claiming "~0 updates / week" for a feed we simply have no
 * history on would be asserting something we don't know. Anything under a
 * weekly item is reported as a bound rather than rounded down to zero.
 */
export const feedCadence = (velocity) => {
    const rate = parseFloat(velocity) || 0;

    if (rate <= VELOCITY_FLOOR) {
        return '';
    }

    if (rate < 1) {
        return '< 1 update / week';
    }

    const perWeek = Math.round(rate);

    return `~${perWeek} update${perWeek === 1 ? '' : 's'} / week`;
};

/**
 * Is the social already an output of this route?
 *
 * @param {object} route
 * @param {number} socialID postAccount id
 * @return {boolean}
 */
export const isSocialOnRoute = (route, socialID) => {
    const outputs = (route && route.outputs) || [];

    for (const output of outputs) {
        if (output && output.social && parseInt(output.social.id, 10) === socialID) {
            return true;
        }
    }

    return false;
};

/**
 * Is the social one of this route's inputs?
 *
 * Accounts are compared on their platform id, which is what the server matches
 * on when it refuses an output that would publish back into its own source. A
 * route sourced from this account is out of bounds in full rather than by the
 * offending input alone: an output belongs to the route, so ticking any of its
 * sibling feeds would build the same loop.
 *
 * @param {object} route
 * @param {string} externalID the social's platform account id
 * @return {boolean}
 */
export const isSocialAnInputOnRoute = (route, externalID) => {
    if (!externalID) {
        return false;
    }

    const inputs = (route && route.inputs) || [];

    for (const input of inputs) {
        // only inputs pulled from a connected account carry an externalID, so a
        // plain feed can never match here
        if (input && input.externalID && String(input.externalID) === String(externalID)) {
            return true;
        }
    }

    return false;
};

/**
 * Does this route already have a master tweeter?
 *
 * A twitter output created without a role is defaulted to OR_ROLE_RETWEET by
 * the server, and PostQueue::doRetweets only fires retweeters off the route's
 * OR_ROLE_NORMAL master — so a retweeter added to a route that has no master
 * silently never publishes. Callers use this to hand the first twitter output on
 * a route the master role and leave later ones to retweet it.
 *
 * Role, not activeFlag, decides this: a disabled master is still the master the
 * server would demote, so a second account must not claim the role.
 *
 * @param {object} route
 * @param {number} twitterSvcID constants.POST_SVC_TWITTER
 * @param {number} normalRole   constants.OR_ROLE_NORMAL
 * @return {boolean}
 */
export const routeHasMasterTweeter = (route, twitterSvcID, normalRole) => {
    const outputs = (route && route.outputs) || [];

    for (const output of outputs) {
        if (!output || !output.social) {
            continue;
        }

        if (parseInt(output.social.psID, 10) === parseInt(twitterSvcID, 10)
            && parseInt(output.role, 10) === parseInt(normalRole, 10)) {
            return true;
        }
    }

    return false;
};

/**
 * Flatten the automatedRoutes list into the feeds a social could be added to.
 *
 * @param  {Array}  routes     automatedRoutes.list
 * @param  {number} socialID   postAccount id the feeds would be added to
 * @param  {string} externalID the social's platform account id, so routes it is
 *                             the source of can be left out
 * @param  {number} nowMs      injectable clock, for the relative timestamps
 * @return {{feeds: Array, connectedCount: number, loopCount: number}} `feeds`
 *         sorted by name. `connectedCount` is how many feeds the social already
 *         publishes and `loopCount` how many it is the source of — both only
 *         matter to a caller explaining an empty candidate list.
 */
export const collectSuggestedFeeds = (routes, socialID, externalID = '', nowMs = Date.now()) => {
    const id = parseInt(socialID, 10),
        feeds = [],
        seen = {};

    let connectedCount = 0,
        loopCount = 0;

    for (const route of routes || []) {
        if (!route || !route.inputs || !route.inputs.length) {
            continue;
        }

        const connected = isSocialOnRoute(route, id),
            loops = isSocialAnInputOnRoute(route, externalID);

        for (const input of route.inputs) {
            const inputID = parseInt(input.id, 10);

            // an input lives on exactly one route, but the list can carry the
            // same route twice while a refresh is in flight
            if (seen[inputID]) {
                continue;
            }
            seen[inputID] = true;

            if (loops) {
                loopCount++;
                continue;
            }

            if (connected) {
                connectedCount++;
                continue;
            }

            feeds.push({
                id: `input-${inputID}`,
                inputID,
                routeID: parseInt(route.id, 10),
                routeName: route.name,
                name: input.name,
                type: input.type,
                subtype: input.subtype,
                icon: input.icon || '',
                lastPullTs: parseInt(input.lastPullTs, 10) || 0,
                updatedAgo: relativeTime(input.lastPullTs, nowMs),
                inputVelocity: parseFloat(input.inputVelocity) || 0,
                activeFlag: Boolean(input.activeFlag),
                sysActionFlag: Boolean(input.sysActionFlag)
            });
        }
    }

    feeds.sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}));

    return {feeds, connectedCount, loopCount};
};

/**
 * Split the candidates into a "most active" highlight reel and the remainder.
 *
 * Ranks on velocity — the 7-day item rate — and breaks ties on freshness. A feed
 * with neither signal is never highlighted, and an account whose feeds all sit
 * at the velocity floor degrades to a pure recency order rather than an
 * arbitrary one.
 *
 * @return {{recommended: Array, rest: Array}}
 */
export const pickMostActive = (feeds, count = RECOMMENDED_COUNT) => {
    const recommended = feeds
            .filter((feed) => feed.inputVelocity > VELOCITY_FLOOR || feed.lastPullTs > 0)
            .sort((a, b) => (b.inputVelocity - a.inputVelocity) || (b.lastPullTs - a.lastPullTs))
            .slice(0, count),
        highlighted = new Set(recommended.map((feed) => feed.id));

    return {
        recommended,
        rest: feeds.filter((feed) => !highlighted.has(feed.id))
    };
};

/**
 * The route ids behind a selection — one output per route, however many of its
 * inputs the user ticked.
 *
 * @return {Array<number>}
 */
export const selectedRouteIDs = (feeds) => {
    const routeIDs = [];

    for (const feed of feeds) {
        if (!routeIDs.includes(feed.routeID)) {
            routeIDs.push(feed.routeID);
        }
    }

    return routeIDs;
};
